// ─── Il giorno scelto, che è anche il comando per cambiarlo ──────────────────
//
// 🏠 Sta qui, e non dentro il calendario, dal 17/09/2026: la cassa ha lo stesso
// bisogno — una giornata per volta, da sfogliare — e due copie della stessa
// barra sarebbero due posti da tenere d'accordo. Il pezzo non è cambiato nel
// trasloco; è cambiato solo chi può usarlo.

import { useRef } from 'react';
import { CampoData, SA_APRIRE_IL_CALENDARIETTO } from './CampoData.js';
import { giornoBreve, giornoPerEsteso } from '../lib/time.js';

/**
 * ⭐⭐ **LA DATA SCRITTA È IL COMANDO** — e prima erano due comandi per la stessa
 * cosa, uno accanto all'altro.
 *
 * 🗣️ Richiesta sua del 16/09/2026, guardando la barra andata a capo:
 *   *«puoi ottimizzare su una riga tutte queste informazioni, così risparmiamo
 *   ancora spazio»*.
 *
 * 📏 **Misurata pezzo per pezzo prima di togliere niente**: `mercoledì 16
 * settembre 2026` costava **279 px** e `giorno 16/09/2026` altri **231**, per
 * dire lo stesso giorno due volte. In tutto la barra voleva **1276 px** di
 * contenuto per stare su una riga — con il menu a sinistra, una finestra da
 * ~1500 px — e andando a capo passava da **44 a 89 px**. Col pannello aperto
 * arrivava a **153**: tre righe, cioè quasi tre ore di giornata in meno.
 *
 * ⚠️ **`showPicker()` NON SI DÀ PER SCONTATO.** È il modo di aprire il
 * calendarietto di sistema da un bottone, e c'è su Safari solo dalla 16. Dove
 * non c'è, questo componente torna al campo data di prima — scritto due volte e
 * largo, ma funzionante. 📌 *Una comodità che si porta via la funzione non è una
 * comodità: qui la via vecchia resta, e la sceglie il browser.*
 */
export function GiornoScelto({
  data,
  vaiA,
}: {
  data: string;
  vaiA(giorno: string): void;
}): React.JSX.Element {
  const campo = useRef<HTMLInputElement>(null);

  if (!SA_APRIRE_IL_CALENDARIETTO) {
    return (
      <label className="campo orizzontale">
        <span className="tenue piccolo">giorno</span>
        {/* ⭐ Anche il ripiego scrive in italiano: senza calendarietto si batte
            la data, e batterla `gg/mm/aaaa` è comunque meglio che leggerla
            nella lingua del browser. */}
        <CampoData value={data} onChange={(g) => g && vaiA(g)} />
      </label>
    );
  }

  return (
    <div className="giorno-scelto">
      <h2>
        <button
          type="button"
          title="Cambia giorno"
          onClick={() => {
            try {
              campo.current?.showPicker();
            } catch {
              /* il browser può rifiutare (fuori da un gesto, o campo non
                 raggiungibile): allora almeno ci si porta sopra il fuoco, e il
                 giorno si cambia da tastiera invece che restare bloccati. */
              campo.current?.focus();
            }
          }}
        >
          {/*
            ⭐ DUE SCRITTURE DELLO STESSO GIORNO, e a scegliere è la larghezza.
            Col pannello aperto su un portatile la barra non ha più i 700 px che
            vuole «mercoledì 16 settembre 2026», e andrebbe a capo — cioè
            costerebbe un'ora di calendario per un nome di giorno per esteso.
            ⚠️ La scelta la fa il CSS, non il JavaScript: così non serve misurare
            la finestra a ogni disegno, e la scritta giusta c'è già al primo.
          */}
          <span className="lungo">{giornoPerEsteso(data)}</span>
          <span className="breve">{giornoBreve(data)}</span> <span aria-hidden="true">▾</span>
        </button>
      </h2>
      {/* Il campo vero resta nel documento: è lui che il calendarietto apre, ed
          è lui che dice al browser dove disegnarlo. Fuori dal giro del Tab
          perché il comando è il bottone qui sopra, non due cose sovrapposte. */}
      <input
        ref={campo}
        type="date"
        className="data-sotto"
        value={data}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => e.target.value && vaiA(e.target.value)}
      />
    </div>
  );
}
